import { useEffect, useState } from "react";
import type { ChangeEvent, ReactElement } from "react";
import type { ApiClient, LogoVariante, Nummernkreis,
              UnternehmenStammdaten } from "@kundeninfo/api-client";
import { aktionsfarbenAus, darfAbbrechen, Feld, Modal, ModalFuss, Objektliste,
         Objektzeile, useBeforeUnload, useFormularZustand, useBestaetigung } from "@kundeninfo/ui";
import { Auswahl, Eingabe, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { logoWirksam } from "../../logoQuelle.js";
import { BILD_ACCEPT } from "../../bildAuswahl.js";
import type { LogoWirksam } from "../../logoQuelle.js";
import { landName } from "../../format.js";
import type { VerlassenWaechter } from "../../router.js";
import { AdressListe } from "../AdressListe.js";
import type { AdressClient } from "../AdressListe.js";
import { DatevBlock } from "./datev.js";
import type { DatevBlockClient } from "./datev.js";
import { ENTITY_LABEL, KEINE_ZAHL, KREISE, Meldung, alsZahl, eingabeUnlesbar,
         meldungAus } from "./gemeinsam.js";
import type { VerwaltungBereichTeile } from "./gemeinsam.js";
import "../Verwaltung.css";

/**
 * Der Verwaltungsbereich UNTERNEHMEN: anlegen, pflegen, stilllegen, Logos verwalten.
 *
 * **Gestalt seit dem Entscheid vom 06.09.2026.** Die Objektliste steht LINKS im Inhalt,
 * rechts daneben das Formular des gewählten Unternehmens. Die Trennung in Liste und
 * Einzelobjekt stammt aus Etappe 2, Aufgabe 2 und bleibt: Bis dahin standen ALLE
 * Unternehmen als aufgeklappte Formularliste im Inhalt untereinander; ein Feld „Name" gab
 * es dort so oft wie Unternehmen, und jede Suche danach war mehrdeutig. Nur der ORT der
 * Liste hat sich geändert — sie stand zwischenzeitlich als zweite Ebene in der
 * Verwaltungs-Spalte und war dort neben dem Bereichs-Untermenü redundant und zu schmal.
 *
 * Deshalb ein HOOK und keine Komponente: siehe `VerwaltungBereichTeile` in gemeinsam.tsx.
 *
 * **Geändert wird im PANEL** (Hausregel „striktes Modalpanel", Entscheid vom 06.09.2026):
 * Das Detail rechts LIEST — Kopf, Anschrift, USt-IdNr., Bankverbindung, Kontakt und die
 * Logos —, und „Ändern" öffnet ein Modal mit den zwölf pflegbaren Feldern und der
 * Fußleiste Speichern/Abbrechen. Bis dahin war das Detail SELBST das Formular: Man konnte
 * hineintippen, weggehen und nie erfahren, dass etwas Ungespeichertes zurückblieb.
 *
 * Die LOGOS bleiben ausdrücklich im Detail. Sie haben kein Speichern und kein Abbrechen —
 * ein Hochladen ist sofort geschehen, ein Entfernen auch. In einem Panel mit der Fußleiste
 * Speichern/Abbrechen wäre das eine Lüge: „Abbrechen" nähme die hochgeladene Datei nicht
 * zurück.
 *
 * Das gewählte Unternehmen steht in der ADRESSE (`/verwaltung/unternehmen/<id>`) — ein
 * Lesezeichen trifft es wieder, und der Bearbeitungszustand ist daraus abgeleitet statt
 * daneben geführt (Etappe-1-Lektion, siehe `bearbeitung` unten).
 *
 * Gepflegt werden die zehn ABSENDER-Felder (street/postalCode/city/country/vatId,
 * Migration 010, iban/bic aus 011, contactName/phone/email aus 014) — das, was auf
 * Angeboten und Rechnungen als Absenderanschrift, USt-IdNr., Zahlungsverbindung und
 * Verkäufer-Kontakt erscheint — und seit dem Paket Unternehmens-Verwaltung zusätzlich
 * `name` und `colorHex`.
 *
 * `shortCode` bleibt ANZEIGE. Er steckt am Anfang jeder bereits vergebenen Nummer
 * (ME-2026-0001); ihn zu ändern zerrisse sie. Der Server weist ihn im Rumpf mit 400 ab —
 * die Maske bietet ihn deshalb gar nicht erst als Eingabefeld an (kein deaktiviertes Feld,
 * siehe Feld.tsx: Lesemodus ist Text).
 *
 * Kein Versionsvergleich (last-write-wins wie beim Dateityp, Ruling P4-B sinngemäß) —
 * deshalb auch keine Konfliktauflösung. Der Schreibweg ist VOLLERSATZ für die zehn
 * Stammdaten (leere ausdrücklich als null; ein weggelassenes Feld wäre 400), aber
 * `name`/`colorHex` gehen nur mit, wenn sie GEÄNDERT wurden: Der Vertrag deutet ihr Fehlen
 * als „nicht angefasst", und bei last-write-wins wäre ein unnötig mitgeschickter Name die
 * Rückkehr eines Standes, den jemand anders gerade umbenannt hat.
 *
 * GELÖSCHT wird nie, nur stillgelegt — an einem Unternehmen hängen Beziehungen, Vorgänge
 * und festgeschriebene Rechnungen. Und weil ein stillgelegtes Unternehmen aus allen
 * Auswahlen verschwindet, fragt die Maske vorher nach.
 *
 * Erfolg und Fehler sind GETRENNTE Meldungen (role status/alert — Lehre 3b-2-W1): Eine
 * gelungene Speicherung ist kein Alarm, und ein Bildschirmleser soll sie auch nicht als
 * einen vorgelesen bekommen.
 *
 * **CSS:** Alles hier trägt den Block `vw__` (views/Verwaltung.css) — Liste wie Formular
 * stehen im Inhalt. Der Spaltenblock `vws__` gehört seit dem 06.09.2026 wieder allein dem
 * Bereichs-Untermenü (Hausregel „CSS-Blocknamen je Ansicht getrennt").
 */

/**
 * Die sechs Wege dieses Bereichs — so lässt er sich im Test mit sechs vi.fn zeichnen,
 * ohne den ganzen ApiClient nachzubauen (Muster: BereichArtikelClient).
 *
 * Seit Aufgabe 10 kommen die fünf Adressmethoden aus `AdressClient` dazu — das Detail
 * reicht denselben Klienten unverändert an den Baustein `AdressListe` weiter (Besitzer
 * `companies`), es braucht sie sonst nirgends selbst. `ApiClient` erfüllt die Erweiterung
 * bereits; nur der `Pick`-Typ hier wächst (dasselbe Muster wie `AnsprechpartnerClient` in
 * KundeAnsprechpartner.tsx).
 */
export type UnternehmenClient = Pick<ApiClient,
  | "listCompaniesMaster" | "updateCompanyMaster" | "createCompany" | "setCompanyActive"
  | "uploadCompanyLogo" | "deleteCompanyLogo"> & AdressClient
  // Der Block „DATEV" (Aufgabe 9) reicht denselben Klienten unverändert an `DatevBlock`
  // weiter — dieselbe Arbeitsteilung wie bei `AdressClient` oben.
  & DatevBlockClient;

/** Die pflegbaren Felder als Maskenstand — Zeichenketten statt string|null, denn ein
 *  Eingabefeld kennt kein null. Zurückverwandelt wird BEIM SPEICHERN (leer → null).
 *  `name` und `colorHex` sind mit dem Paket Unternehmens-Verwaltung dazugekommen;
 *  `shortCode` steht bewusst NICHT hier — er ist unveränderlich. */
type UnternehmenEntwurf = {
  name: string; colorHex: string;
  street: string; houseNumber: string; postalCode: string; city: string; country: string;
  vatId: string;
  // Bankverbindung (Migration 011) — seit der Masken-Aufgabe des Pakets beleg-vertrag-v1
  // sichtbar pflegbar; der Server-Vertrag ist ein ZEHN-Felder-Vollersatz.
  iban: string; bic: string;
  // Verkäufer-Kontakt (Migration 014, BG-6/BT-41..43) — Pflichtangabe der XRechnung
  // (BR-DE-5/6/7). `contactName` heisst so und nicht `name`, weil `name` an derselben
  // Zeile schon der Firmenname ist.
  contactName: string; phone: string; email: string;
};

type UnternehmenFeld =
  "name" | "colorHex"
  | "street" | "houseNumber" | "postalCode" | "city" | "country" | "vatId" | "iban" | "bic"
  | "contactName" | "phone" | "email";

/** Die zehn Stammdaten-Felder plus die WAHLFREIE Hausnummer (Nachtrag Aufgabe 6, Festlegung
 *  F2 wie am Kunden) — die Feldmenge, die der Server am Pflegeweg entgegennimmt. */
const STAMMDATEN_FELDER = ["street", "houseNumber", "postalCode", "city", "country",
                           "vatId", "iban", "bic", "contactName", "phone", "email"] as const;

const UNTERNEHMEN_FELDER: readonly UnternehmenFeld[] =
  ["name", "colorHex", ...STAMMDATEN_FELDER];

function unternehmensentwurfAus(u: UnternehmenStammdaten): UnternehmenEntwurf {
  return { name: u.name, colorHex: u.colorHex,
           // `streetName`/`houseNumber` und NICHT `street`: `street` ist die fertige
           // Zeile („Hafenstraße 12a") und stünde im Straßenfeld mit der Hausnummer
           // darin — beim Speichern käme sie doppelt heraus (Nachtrag Aufgabe 6,
           // dasselbe Muster wie in KundeStammdaten.tsx).
           street: u.streetName ?? "", houseNumber: u.houseNumber ?? "",
           postalCode: u.postalCode ?? "", city: u.city ?? "",
           country: u.country, vatId: u.vatId ?? "",
           iban: u.iban ?? "", bic: u.bic ?? "",
           contactName: u.contactName ?? "", phone: u.phone ?? "",
           email: u.email ?? "" };
}

function unternehmensentwurfGleich(a: UnternehmenEntwurf, b: UnternehmenEntwurf): boolean {
  return a.name === b.name && a.colorHex === b.colorHex
    && a.street === b.street && a.houseNumber === b.houseNumber
    && a.postalCode === b.postalCode && a.city === b.city
    && a.country === b.country && a.vatId === b.vatId
    && a.iban === b.iban && a.bic === b.bic
    && a.contactName === b.contactName && a.phone === b.phone && a.email === b.email;
}

/**
 * Die vier Logo-Varianten, deutsch beschriftet. Als `Record` über dem Vertragstyp und
 * NICHT als abgetippte Liste: Eine fünfte Variante im Vertrag lässt diese Tabelle nicht
 * mehr übersetzen und zwingt zu einer Zeile hier — eine Liste bliebe still unvollständig.
 */
const LOGO_LABEL: Record<LogoVariante, string> = {
  "wortmarke": "Wortmarke (hell)",
  "wortmarke-dunkel": "Wortmarke (dunkel)",
  "bildmarke": "Bildmarke (hell)",
  "bildmarke-dunkel": "Bildmarke (dunkel)",
};

const LOGO_VARIANTEN = Object.keys(LOGO_LABEL) as LogoVariante[];

/**
 * Die Vorschau einer Variante — dieselbe Auflösung wie in der Kopfzeile (`logoWirksam`),
 * nur mit einem Stempel hinter dem HOCHGELADENEN Pfad.
 *
 * Bis zum 11.09.2026 stand hier ausschliesslich Hochgeladenes, weil die Verwaltungsgestalt
 * die statischen Dateinamen gar nicht führte. Das war der Fehler: An einer Variante, für
 * die eine Standarddatei ausgeliefert wird, stand „kein Logo" — und wer das liest, lädt
 * ein zweites Logo hoch, das er nicht braucht. Die Maske zeigt jetzt, was WIRKSAM ist, und
 * sagt dazu, woher es kommt; entfernen lässt sich weiterhin nur Hochgeladenes (siehe
 * `logoLoeschen`), denn nur das liegt in der Ablage.
 */
function logoVorschau(
  u: UnternehmenStammdaten, variante: LogoVariante, stempel: number | undefined,
): LogoWirksam | null {
  const wirksam = logoWirksam(u, variante);
  if (wirksam === null || wirksam.herkunft !== "hochgeladen" || stempel === undefined) {
    return wirksam;
  }
  // Der Pfad einer Variante ist fest — ohne diesen Anhang zeigte der Browser nach einem
  // Austausch weiter das Bild aus seinem Zwischenspeicher. Der Server sieht ihn nie an.
  // NUR am hochgeladenen Pfad: Die statische Datei wechselt ihren Inhalt nicht, sie
  // wechselt mit der Auslieferung ihren Namen.
  return { ...wirksam, url: `${wirksam.url}?v=${stempel}` };
}

/** Woher das wirksame Logo kommt, in einem Satz für die Zeile. Der Dateiname steht dabei,
 *  weil er die einzige Auskunft ist, WELCHE Standarddatei greift — sonst müsste man
 *  raten, ob die Marke aus dem Auslieferungspaket zum Unternehmen passt. */
function herkunftText(wirksam: LogoWirksam): string {
  return wirksam.herkunft === "hochgeladen"
    ? "hochgeladen" : `Standarddatei ${wirksam.datei}`;
}

/** Der Anlege-Dialog: die drei Pflichtfelder, die sieben Stammdaten und je Kreis ein
 *  Startwert. Alles als Zeichenkette — es sind Eingabefelder. */
type UnternehmenDialog = UnternehmenEntwurf & {
  shortCode: string;
  startwerte: Record<Nummernkreis["entity"], string>;
};

/**
 * Der leere Dialog. Die Vorgaben sind mit Bedacht gewählt: `DE` als Land (der Bestand ist
 * deutsch), `#7AB41E` als Farbe — irgendein gültiger Wert MUSS im Farbwähler stehen, denn
 * ein `<input type="color">` kennt kein „leer" und zeigte sonst Schwarz an, und Schwarz
 * wäre eine stille Vorgabe, die niemand gewählt hat. Startwert 0 je Kreis (Spec: Vorgabe 0).
 */
function leererUnternehmensdialog(): UnternehmenDialog {
  return {
    name: "", shortCode: "", colorHex: "#7AB41E",
    street: "", houseNumber: "", postalCode: "", city: "", country: "DE", vatId: "",
    iban: "", bic: "",
    contactName: "", phone: "", email: "",
    startwerte: { CUSTOMER: "0", OFFER: "0", ORDER: "0", INVOICE: "0" },
  };
}

/**
 * Ob im Anlege-Panel noch nichts steht — verglichen mit genau dem Stand, mit dem es
 * geöffnet wurde. Gebraucht für die Rückfrage vor dem Abbrechen (Hausregel „striktes
 * Modalpanel"): Wer nichts eingegeben hat, soll auch nichts gefragt werden.
 *
 * Feld für Feld über die BENANNTEN Listen und nicht über einen Vergleich der ganzen
 * Objekte: Kommt ein Feld hinzu, steht es in `UNTERNEHMEN_FELDER` bzw. in `KREISE` — und
 * zählt damit ohne Zutun mit.
 */
function anlegedialogLeer(d: UnternehmenDialog): boolean {
  const leer = leererUnternehmensdialog();
  return UNTERNEHMEN_FELDER.every((f) => d[f] === leer[f])
    && d.shortCode === leer.shortCode
    && KREISE.every((k) => d.startwerte[k] === leer.startwerte[k]);
}

/** Die Feldnamen, die eine 422 beim Anlegen tragen kann: die zwölf der Maske plus das
 *  Kürzel und die vier Startwerte (Feldname `startValue.<KREIS>`, siehe verwaltung.ts). */
const ANLEGEN_FELDER: readonly string[] = [
  ...UNTERNEHMEN_FELDER, "shortCode", ...KREISE.map((k) => `startValue.${k}`),
];

/** Die Beschriftungen für die Sammelmeldung (ein Befund, der an keinem Feld steht, lautet
 *  „<Beschriftung>: <Meldung>“ — nie mit dem technischen Feldnamen des Servers). */
const UNTERNEHMEN_BESCHRIFTUNGEN: Readonly<Record<string, string>> = {
  name: "Name", colorHex: "Farbe", shortCode: "Kürzel", street: "Straße",
  houseNumber: "Hausnummer", postalCode: "Postleitzahl", city: "Ort", country: "Land",
  vatId: "USt-IdNr.", iban: "IBAN", bic: "BIC", contactName: "Kontakt (Name der Stelle)",
  phone: "Kontakt-Telefon", email: "Kontakt-E-Mail",
  ...Object.fromEntries(KREISE.map((k) => [`startValue.${k}`, `Startwert ${ENTITY_LABEL[k]}`])),
};

/**
 * Die Farbe eines Unternehmens: `me-eingabe art="farbe"` (Entscheid 29, Befund 17; ME-Etappe 4,
 * P3) — ein Hex-Feld mit Farbmuster, das Muster öffnet die Palette benannter Farben. Als erste
 * Reihe stehen die FARBEN DER UNTERNEHMEN (`unternehmensfarben`, je `<option>`), damit eine
 * Hausfarbe mit einem Griff übernommen ist; „Weitere Farbe …“ öffnet den Wähler des Systems.
 * Zwischenformen wie `#7AB` darf das Feld tragen (es ist Text); der Server prüft.
 *
 * `gesperrt` (Aufgabe 8, Review A6): Während des Speicherns bzw. bei fremdem Stand ist das
 * Feld schreibgeschützt (readOnly + aria-disabled, der Fokus bleibt), das Muster öffnet keine
 * Palette — das Element sperrt beides.
 */
function Farbwahl(
  p: { id: string; label: string; wert: string; onChange: (w: string) => void;
       fehler?: string; gesperrt?: boolean;
       unternehmensfarben: readonly { name: string; colorHex: string }[] },
): ReactElement {
  const gesperrt = p.gesperrt === true;
  const gueltig = /^#[0-9A-Fa-f]{6}$/.test(p.wert);
  // Jede Hausfarbe einmal (zwei Unternehmen mit derselben Farbe stünden sonst doppelt).
  const farben = p.unternehmensfarben.filter((u, i, alle) =>
    /^#[0-9A-Fa-f]{6}$/.test(u.colorHex)
    && alle.findIndex((v) => v.colorHex.toUpperCase() === u.colorHex.toUpperCase()) === i);
  return (
    <div className="vw__farbwahl">
      <div className="vw__farbwahl-zeile">
        <Eingabe id={p.id} className="vw__farbe" art="farbe" beschriftung={p.label}
          wert={p.wert}
          {...(p.fehler !== undefined ? { fehler: p.fehler } : {})}
          {...(gesperrt ? { gesperrt: true } : {})}
          onGeaendert={(d) => { if (!gesperrt) p.onChange(d.wert); }}>
          {farben.map((u) => <option key={u.colorHex} value={u.colorHex}>{u.name}</option>)}
        </Eingabe>
        {gueltig && (
          // Die Vorschau des ABGELEITETEN Aktionstons — dieselbe Rechnung, die beim
          // Anmelden die Tokens setzt (aktionsfarbenAus aus @kundeninfo/ui). Sie steht
          // hier, damit sichtbar ist, was die Marke in der Oberfläche tatsächlich bewirkt:
          // Ein helles Grün wird zum dunkleren Aktionston, sonst trüge es keinen Text.
          <span className="vw__farbprobe"
            style={{ background: aktionsfarbenAus(p.wert).aktion, color: "#FFFFFF" }}>
            Aktion
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Die Eigenschaften des Bereichs — dieselbe Gestalt wie bei den Bereichen der Etappe 1
 * (siehe views/BereichArtikel.tsx): `aktiv` als Riegel gegen mitlaufende Abfragen an
 * fremden Adressen, die Kennung aus der ADRESSE, und ein Rückruf für den Adresswechsel.
 * Navigiert wird nicht hier — die Adresse kennt App.tsx.
 */
export type VerwaltungUnternehmenProps = {
  /** Steht der Bereich gerade auf dem Bildschirm? Der Hook wird — Regeln der Hooks — auch
   *  an anderen Adressen gerufen; ohne diesen Riegel liefe seine Liste überall mit. */
  aktiv: boolean;
  client: UnternehmenClient;
  /** Das geöffnete Unternehmen aus der Adresse (`/verwaltung/unternehmen/:id`). */
  unternehmenId: string | null;
  /** Das Arbeitsunternehmen aus der Kopfleiste (ME-Etappe 1, Aufgabe 7): Ohne Unternehmen
   *  in der Adresse steht ES zuerst offen. `null`: Bestand (nur der Hinweis). */
  arbeitsunternehmenId: string | null;
  onOeffnen: (unternehmenId: string | null) => void;
  /** Der Verlassen-Wächter des Routers (Hausmuster, siehe KundeStammdaten): Bei offener
   *  Bearbeitung fragt jede Navigation nach. Weglassbar — dann fragt niemand, was für
   *  einen Prüfharnisch ohne Router genau richtig ist. */
  registriereVerlassenWaechter?: (frage: VerlassenWaechter) => () => void;
  /**
   * Ob der angemeldete Benutzer ADMIN ist — steuert das Schreibrecht der Adress-Karte
   * (`nurLesen`, Aufgabe 10). Der Server weist Schreiben an Adressen für alle anderen mit
   * 403 ab.
   *
   * Der ganze Bereich ist an anderer Stelle bereits auf ADMIN verriegelt (`aktiv: istAdmin
   * && …` in App.tsx) — dieses Attribut fragt trotzdem ausdrücklich nach der Rolle, statt
   * sie aus der Umgebung zu erschliessen: Der Hook kennt seinen Aufrufer nicht und soll
   * sich nicht auf einen Wächter verlassen, der anderswo vielleicht fehlt oder sich ändert.
   */
  istAdmin: boolean;
};

export function useVerwaltungUnternehmen(
  p: VerwaltungUnternehmenProps,
): VerwaltungBereichTeile {
  const { client, aktiv, istAdmin } = p;

  const [zeilen, setZeilen] = useState<UnternehmenStammdaten[] | null>(null);
  /*
   * Das GEÖFFNETE Unternehmen: das aus der Adresse, sonst das Arbeitsunternehmen (ME-Etappe
   * 1, Aufgabe 7) — sobald die Liste es kennt; bis dahin der Hinweis, nicht die Auskunft
   * „gibt es nicht". Bewusst ohne Navigation: Die Adresse bleibt `/verwaltung/unternehmen`
   * (siehe die gleiche Überlegung im Layout-Bereich). Entwürfe hängen je Kennung an
   * `entwuerfe`, ein Wechsel des Arbeitsunternehmens verliert also nichts.
   */
  const unternehmenId = p.unternehmenId
    ?? ((zeilen ?? []).some((u) => u.id === p.arbeitsunternehmenId)
      ? p.arbeitsunternehmenId : null);
  /** Die zulässigen Länderkürzel — aus der ANTWORT, wie die Platzhalter der
   *  Textbausteine: web darf domain nicht importieren, und eine abgetippte Liste
   *  veraltete mit dem nächsten neuen Land still. */
  const [laender, setLaender] = useState<string[]>([]);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [entwuerfe, setEntwuerfe] = useState<Record<string, UnternehmenEntwurf>>({});
  /** Fehlschlag des Stilllegens/Einschaltens je Unternehmen (kein Formular). */
  const [meldungen, setMeldungen] = useState<Record<string, string>>({});
  const [erfolge, setErfolge] = useState<Record<string, string>>({});

  /** Der Anlege-Dialog (null = zu). */
  const [dialog, setDialog] = useState<UnternehmenDialog | null>(null);

  /** Das Unternehmen, dessen Stilllegung gerade nachgefragt wird. */
  const [stilllegen, setStilllegen] = useState<UnternehmenStammdaten | null>(null);
  /**
   * WESSEN Änderungspanel offen steht — `null` heisst „keines" (Hausregel „striktes
   * Modalpanel", 06.09.2026: Bearbeiten läuft im Modal, nicht im Inhalt).
   *
   * Eine KENNUNG und kein blosses Ja/Nein, aus demselben Grund wie bei der
   * Stilllegen-Rückfrage daneben: Das Panel gehört dem Unternehmen, über dem es geöffnet
   * wurde. Es ist bewusst KEIN Adressbestandteil — die Adresse benennt das Objekt, nicht
   * den Zustand der Oberfläche.
   */
  const [aendern, setAendern] = useState<string | null>(null);
  const [schaltet, setSchaltet] = useState<string | null>(null);

  /** Fehler und Arbeitsstand je Unternehmen UND Variante (Schlüssel `<id>|<variante>`). */
  const [logoFehler, setLogoFehler] = useState<Record<string, string>>({});
  const [logoArbeitet, setLogoArbeitet] = useState<string | null>(null);
  /**
   * Ein Stempel je Variante, der an die Vorschau-Adresse gehängt wird. Der Pfad einer
   * Variante ist FEST — ohne den Stempel zeigte der Browser nach dem Austausch eines Logos
   * weiter das alte Bild aus seinem Zwischenspeicher, und der Eindruck wäre, der Upload
   * hätte nicht gewirkt. Nur die VERWALTUNG braucht das (dort wird ausgetauscht); die
   * Kopfzeile lädt ohnehin bei jeder Anmeldung neu.
   */
  const [stempel, setStempel] = useState<Record<string, number>>({});

  /**
   * Die Version der Adress-Karte je Unternehmen bestimmt NICHT allein — ein einzelner
   * Zähler reicht, weil er im `key` der Karte NEBEN der Unternehmens-Kennung steht
   * (`${u.id}-${adressVersion}`, Muster `wegeVersion` in KundeAnsprechpartner.tsx): Ein
   * Stammdaten-Speichern erhöht ihn, egal an welchem Unternehmen — nur das gerade
   * geöffnete zeichnet die Karte überhaupt, und dessen `key` ändert sich dadurch und zwingt
   * einen frischen Ladevorgang, ohne dass `client` oder `ownerId` sich geändert hätten.
   */
  const [adressVersion, setAdressVersion] = useState(0);

  const listCompaniesMaster = client.listCompaniesMaster;
  useEffect(() => {
    if (!aktiv) return;
    let abgebrochen = false;
    listCompaniesMaster()
      .then((antwort) => {
        if (!abgebrochen) {
          setZeilen(antwort.items);
          setLaender(antwort.laender);
          // Ein geglücktes Nachladen HEILT den alten Fehler (Fixrunde 1). Solange die
          // Bereiche eigene Komponenten waren, erledigte das der Ausbau aus dem Baum —
          // seit sie als Hook über der ganzen Anwendung leben, überlebt ihr Zustand jeden
          // Bereichswechsel, und der Fehlerstreifen stünde für immer über einer Liste, die
          // längst wieder da ist.
          setLadeFehler(null);
        }
      })
      .catch((fehler: unknown) => {
        if (!abgebrochen) {
          setLadeFehler(
            meldungAus(fehler, "Die Unternehmen konnten nicht geladen werden."));
        }
      });
    return () => { abgebrochen = true; };
  }, [aktiv, listCompaniesMaster]);

  /*
   * Beim VERLASSEN des Bereichs fällt alles Angefangene weg (Etappe-1-Lektion aus
   * Aufgabe 3).
   *
   * Entwürfe, Quittungen, Fehler und der halb ausgefüllte Anlege-Dialog sind
   * Bearbeitungszustände OHNE eigene Adresse. Blieben sie über einen Bereichs- oder
   * Menüwechsel hinweg stehen, käme man mit der Quittung — oder der halben Maske — einer
   * längst verlassenen Absicht zurück.
   */
  useEffect(() => {
    if (aktiv) return;
    setEntwuerfe({});
    aenderFormular.zuruecksetzen();
    setMeldungen({});
    setErfolge({});
    setLogoFehler({});
    setDialog(null);
    anlegeFormular.zuruecksetzen();
    setStilllegen(null);
    setAendern(null);
    // Auch der LADEFEHLER (Fixrunde 1): Beim nächsten Betreten läuft die Abfrage neu, und
    // bis ihre Antwort da ist, soll die Auskunft „lädt" heissen — nicht der Fehler von
    // vorhin. Er würde sonst die dreistufige Deep-Link-Auskunft verstellen.
    setLadeFehler(null);
  }, [aktiv]);

  // Die Stilllegen-Rückfrage fällt mit der Kennung (Abschlussreview Etappe 2): Der
  // Render-Guard versteckt sie bei fremder Adresse nur — ohne dieses Nullen stünde sie
  // nach Zurück/Vorwärts unaufgefordert wieder da. Gleiches Muster wie die Archiv-Frage
  // der Konditionen.
  useEffect(() => {
    setStilllegen(null);
    // Das Änderungspanel fällt aus demselben Grund mit: Der Render-Guard unten versteckt es
    // bei fremder Adresse nur, und sein Speichern-Knopf schriebe sonst nach
    // Zurück/Vorwärts an einem Unternehmen, das niemand ansieht.
    setAendern(null);
    aenderFormular.zuruecksetzen();
  }, [unternehmenId]);

  /**
   * Nach einer Änderung an der Adress-Karte: die ganze Liste neu holen — nicht nur das
   * geöffnete Unternehmen.
   *
   * Strasse, Ort, Bankverbindung und Kontakt der zehn Stammdaten-Felder schreiben
   * serverseitig aus der Hauptadresse (Migration 020) — genau die Felder, die das Detail
   * oben als „Anschrift"/„Bankverbindung"/„Kontakt" liest und die die Objektliste links
   * ebenfalls zeigt. Die Karte kennt diese Ableitung nicht und muss sie auch nicht kennen;
   * sie meldet nur „etwas hat sich geändert", und hier wird deshalb der ganze Server-Stand
   * geholt statt ein einzelnes Feld zusammengeführt (dasselbe Vorgehen wie `neuLaden` in
   * AdressListe selbst).
   */
  async function nachAdressaenderung(): Promise<void> {
    try {
      const antwort = await listCompaniesMaster();
      setZeilen(antwort.items);
      setLaender(antwort.laender);
      setLadeFehler(null);
    } catch (fehler) {
      setLadeFehler(meldungAus(fehler, "Die Unternehmen konnten nicht geladen werden."));
    }
  }

  function entwurfVon(u: UnternehmenStammdaten): UnternehmenEntwurf {
    return entwuerfe[u.id] ?? unternehmensentwurfAus(u);
  }

  function istGeaendert(u: UnternehmenStammdaten): boolean {
    return !unternehmensentwurfGleich(entwurfVon(u), unternehmensentwurfAus(u));
  }

  /** Den Entwurf eines Unternehmens fallen lassen — die Zeile lebt danach wieder aus dem
   *  geladenen Stand. Nach dem Speichern (die Antwort ist der neue Stand) und beim
   *  bestätigten Verlassen (siehe den Wächter unten). */
  function vergissEntwurf(id: string): void {
    setEntwuerfe((alt) => { const rest = { ...alt }; delete rest[id]; return rest; });
  }

  const satz = unternehmenId === null
    ? null
    : (zeilen ?? []).find((u) => u.id === unternehmenId) ?? null;

  /**
   * OB gerade bearbeitet wird — als Ableitung aus der ADRESSE, nicht als eigenes Ja/Nein
   * (Etappe-1-Lektion aus Aufgabe 3).
   *
   * Ein freistehendes `bearbeitung: boolean` überlebte dort den Adresswechsel und sperrte
   * die Objektliste für immer. Hier hängt der Zustand am geöffneten Objekt: Ist keines
   * offen (oder ein anderes), ist auch nichts in Bearbeitung — ganz gleich, welcher
   * Entwurf noch im Speicher liegt.
   *
   * Ein „offenes Formular" gibt es in der Verwaltung nicht als eigenen Modus: Die Felder
   * sind immer Eingaben. In Bearbeitung ist deshalb genau, was ungespeicherte Änderungen
   * trägt — dieselbe Bedingung, die unten den Speichern-Knopf freigibt.
   */
  const bearbeitung = satz !== null && istGeaendert(satz);

  /*
   * Das ECHTE Verlassen der Seite (Tab zu, neu laden, andere Anwendung) — Bestandsverhalten
   * aus dem Paket 2a, unverändert übernommen: Es fragt bei JEDEM ungespeicherten Entwurf
   * nach, auch bei einem, der zu einem gerade nicht geöffneten Unternehmen gehört. Der
   * Browser kennt keine Adresse innerhalb der Anwendung; was er verhindern soll, ist der
   * Verlust von Eingaben überhaupt.
   */
  const etwasGeaendert = (zeilen ?? []).some(istGeaendert);
  useBeforeUnload(etwasGeaendert);

  const { bestaetigen, bestaetigung } = useBestaetigung();

  /**
   * Der Verlassen-Wächter des ROUTERS (Hausmuster, siehe KundeStammdaten): Solange das
   * geöffnete Unternehmen ungespeicherte Änderungen trägt, fragt jede Navigation nach —
   * der Rückweg im Kopf des Einspalters ebenso wie die Zurück-Taste und der Sprung auf
   * einen anderen Verwaltungsbereich. (Ein Klick auf ein anderes Unternehmen in der Liste
   * kommt hier NICHT an: Die Zeilen sind während der Bearbeitung gesperrt.) Ohne ihn
   * verwarf ein Rückweg die Eingaben stumm.
   *
   * **Bestätigtes Verlassen VERWIRFT den Entwurf** (Fixrunde 1). Bis dahin räumte nur die
   * Adresse auf — `bearbeitung` passte danach nicht mehr —, der Entwurf selbst blieb je
   * Kennung liegen und stand beim Zurückkommen wieder da. Damit sagte die Rückfrage die
   * Unwahrheit: Es ging gar nichts verloren. Entweder der Satz oder das Verhalten musste
   * weichen; das Verhalten ist das schwächere von beiden — wer „verwerfen" bestätigt, will
   * den Serverstand wiedersehen, nicht seinen alten Tippversuch.
   */
  const registriereVerlassenWaechter = p.registriereVerlassenWaechter;
  useEffect(() => {
    if (!bearbeitung || registriereVerlassenWaechter === undefined
      || unternehmenId === null) return;
    return registriereVerlassenWaechter(async () => {
      const weiter = await bestaetigen({
        titel: "Seite verlassen?",
        text: "Dieses Unternehmen hat ungespeicherte Änderungen. Sie gehen verloren.",
        bestaetigenText: "Verlassen",
        gefaehrlich: true,
      });
      if (weiter) vergissEntwurf(unternehmenId);
      return weiter;
    });
    // `vergissEntwurf` steht bewusst nicht in der Liste: Es ist eine reine
    // Zustandsschreibung und wird bei jedem Zeichnen neu gebaut — mitgeführt liefe der
    // Effekt bei jedem Tastendruck ab und meldete den Wächter sinnlos ab und wieder an.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bearbeitung, registriereVerlassenWaechter, unternehmenId]);

  function aendere(u: UnternehmenStammdaten, teil: Partial<UnternehmenEntwurf>): void {
    const alt = entwurfVon(u);
    setEntwuerfe((e) => ({ ...e, [u.id]: { ...alt, ...teil } }));
    // Der Feldfehler des korrigierten Feldes verschwindet beim Tippen (Aufgabe 8).
    for (const feld of Object.keys(teil)) aenderFormular.feldFehlerRaeumen(feld);
    // Ein „Gespeichert." neben einer frisch geänderten Eingabe wäre eine falsche Zusage.
    setErfolge((m) => ({ ...m, [u.id]: "" }));
  }

  /** Leer heisst null — die Feinprüfung (Trimmen, Längen, PLZ/Ort-Paar) macht die Domäne
   *  über das Repository; hier reicht „nichts eingetippt = kein Wert". */
  function leerAlsNull(wert: string): string | null {
    return wert.trim() === "" ? null : wert;
  }

  /**
   * Formularzustände des ÄNDERUNGSPANELS (ME-Etappe 3, Aufgabe 6) — es steht immer nur
   * eines offen. `laeuft` sperrt Felder, Farbwahl und Fuß; eine 422 hängt am Feld und der
   * Fokus springt dorthin. Kein Versionsvergleich (last-write-wins) — eine 409 gibt es
   * nicht; „Neu laden" (nur für den Fall, dass der Server doch eine schickt) liest die Liste.
   */
  const aenderFormular = useFormularZustand<UnternehmenStammdaten>({
    speichern: async () => {
      const u = satz;
      if (u === null) throw new Error("Kein Unternehmen offen.");
      const e = entwurfVon(u);
      // VOLLERSATZ für die zehn Stammdaten, die leeren ausdrücklich als null. `name`
      // und `colorHex` nur, wenn sie sich geändert haben: Ihr Fehlen bedeutet im Vertrag
      // „nicht angefasst", und bei last-write-wins ist das der Unterschied zwischen
      // „ich lasse den Namen in Ruhe" und „ich schreibe meinen alten Stand zurück".
      const neu = await client.updateCompanyMaster(u.id, {
        street: leerAlsNull(e.street),
        houseNumber: leerAlsNull(e.houseNumber),
        postalCode: leerAlsNull(e.postalCode),
        city: leerAlsNull(e.city),
        country: e.country,
        vatId: leerAlsNull(e.vatId),
        iban: leerAlsNull(e.iban),
        bic: leerAlsNull(e.bic),
        contactName: leerAlsNull(e.contactName),
        phone: leerAlsNull(e.phone),
        email: leerAlsNull(e.email),
        ...(e.name === u.name ? {} : { name: e.name }),
        ...(e.colorHex === u.colorHex ? {} : { colorHex: e.colorHex }),
      });
      // Die Antwort übernehmen (der Server hat normalisiert — getrimmt, Land in
      // Großform) und den Entwurf vergessen: Die Zeile lebt wieder aus dem Bestand.
      setZeilen((alt) => (alt === null ? alt
        : alt.map((x) => (x.id === neu.id ? neu : x))));
      vergissEntwurf(u.id);
      setErfolge((m) => ({ ...m, [u.id]: "Gespeichert." }));
      // Die zehn Stammdaten schreiben auf die Hauptadresse durch (Migration 020) — die
      // Adress-Karte bekommt das nicht von selbst mit, sie lädt nur bei ihrem eigenen
      // `key`-Wechsel neu (siehe `adressVersion` oben).
      setAdressVersion((v) => v + 1);
      return neu;
    },
    neuLaden: nachAdressaenderung,
    bekannteFelder: UNTERNEHMEN_FELDER,
    beschriftungen: UNTERNEHMEN_BESCHRIFTUNGEN,
  });

  async function speichern(u: UnternehmenStammdaten): Promise<void> {
    setMeldungen((m) => ({ ...m, [u.id]: "" }));
    setErfolge((m) => ({ ...m, [u.id]: "" }));
    // Der EINE erfolgreiche Ausgang des strikten Panels. Bei 422 bleibt es stehen: Dort
    // sind die Feldfehler entstanden, dort müssen sie zu lesen und zu beheben sein.
    if (await aenderFormular.aktion() !== undefined) setAendern(null);
  }

  /** Der Startwert eines Kreises als Zahl — leer zählt als 0 (Vorgabe der Spec). */
  function startwertAls(wert: string): number | typeof KEINE_ZAHL {
    const zahl = alsZahl(wert);
    if (zahl === KEINE_ZAHL) return KEINE_ZAHL;
    return zahl ?? 0;
  }

  /**
   * Formularzustände des ANLEGE-Dialogs (ME-Etappe 3, Aufgabe 6): Die vier Startwerte
   * werden zuerst gelesen — eine unlesbare Zahl steht AM FELD (als dieselbe 422-Gestalt
   * wie ein Befund des Servers) und nicht als 400 vom Server, die kein Feld nennt.
   */
  const anlegeFormular = useFormularZustand<string>({
    speichern: async () => {
      const d = dialog;
      if (d === null) throw new Error("Kein Anlege-Dialog offen.");
      const startwerte: Record<string, number> = {};
      const unlesbar: { field: string; message: string }[] = [];
      for (const kreis of KREISE) {
        const zahl = startwertAls(d.startwerte[kreis]);
        if (zahl === KEINE_ZAHL) {
          unlesbar.push({ field: `startValue.${kreis}`, message: "Bitte eine Zahl eintragen." });
        } else startwerte[kreis] = zahl;
      }
      if (unlesbar.length > 0) throw eingabeUnlesbar(unlesbar);

      const angelegt = await client.createCompany({
        name: d.name, shortCode: d.shortCode, colorHex: d.colorHex,
        street: leerAlsNull(d.street),
        houseNumber: leerAlsNull(d.houseNumber),
        postalCode: leerAlsNull(d.postalCode),
        city: leerAlsNull(d.city),
        country: d.country,
        vatId: leerAlsNull(d.vatId),
        iban: leerAlsNull(d.iban),
        bic: leerAlsNull(d.bic),
        contactName: leerAlsNull(d.contactName),
        phone: leerAlsNull(d.phone),
        email: leerAlsNull(d.email),
        // Immer alle vier — der Vertrag lässt `startwerte` weg, wenn ein Formular sie gar
        // nicht anbietet; DIESES Formular zeigt sie, also sind auch vier Nullen eine
        // ausdrückliche Angabe und keine ausgelassene.
        startwerte,
      });
      // Anlegen antwortet nur mit der Kennung (Hausmuster aller Anlege-Routen) — die
      // Liste neu lesen, damit der frische Satz mit seinen Logos und seinem Zustand steht.
      const antwort = await client.listCompaniesMaster();
      setZeilen(antwort.items);
      setLaender(antwort.laender);
      return angelegt.id;
    },
    neuLaden: nachAdressaenderung,
    bekannteFelder: ANLEGEN_FELDER,
    beschriftungen: UNTERNEHMEN_BESCHRIFTUNGEN,
  });

  /** Der eine Weg aus dem Anlege-Panel neben dem Anlegen (Hausregel „striktes
   *  Modalpanel", 06.09.2026) — mit Rückfrage, sobald etwas darin steht. Escape geht
   *  denselben Weg; ein Klick daneben schliesst nicht. */
  async function anlegenAbbrechen(): Promise<void> {
    // Kein Ausgang, solange geschrieben wird — das gilt für die ESCAPE-Taste genauso wie
    // für den (dann gesperrten) Knopf: Beide gehen durch diese Funktion, und die Antwort
    // des Servers braucht ihr Panel noch (Fixrunde 1).
    if (anlegeFormular.laeuft) return;
    if (dialog !== null && !(await darfAbbrechen(!anlegedialogLeer(dialog)))) return;
    setDialog(null);
  }

  async function anlegen(): Promise<void> {
    const id = await anlegeFormular.aktion();
    if (id === undefined) return;
    setDialog(null);
    // Und die ADRESSE geht auf den frischen Satz: Wer eben etwas angelegt hat, will es
    // sehen — und die Kennung, die das Anlegen zurückgibt, ist genau dafür da. Ohne
    // diesen Schritt bliebe die Maske bei der Liste liegen und der neue Eintrag müsste
    // von Hand gesucht werden.
    p.onOeffnen(id);
  }

  /**
   * Stilllegen und Wiedereinschalten laufen über dieselbe Route. NACHGEFRAGT wird nur beim
   * Stilllegen (der Dialog steht dafür in `stilllegen`): Es nimmt das Unternehmen aus allen
   * Auswahlen, das Wiedereinschalten stellt nur her, was vorher war.
   */
  async function umschalten(u: UnternehmenStammdaten, aktivSchalten: boolean): Promise<void> {
    setStilllegen(null);
    setMeldungen((m) => ({ ...m, [u.id]: "" }));
    setSchaltet(u.id);
    try {
      const neu = await client.setCompanyActive(u.id, aktivSchalten);
      setZeilen((alt) => (alt === null ? alt : alt.map((x) => (x.id === neu.id ? neu : x))));
    } catch (fehler) {
      const text = meldungAus(fehler, "Der Zustand konnte nicht geändert werden.");
      setMeldungen((m) => ({ ...m, [u.id]: text }));
    } finally {
      setSchaltet(null);
    }
  }

  function logoSchluessel(id: string, variante: LogoVariante): string {
    return `${id}|${variante}`;
  }

  /**
   * Der Upload einer Variante. Die Riegel des Servers (kein SVG, aktive Inhalte, kaputtes
   * XML) kommen als 422 mit einer erklärenden Meldung, aber OHNE Feldangabe — sie gehören
   * trotzdem an die Variante, an der sie entstanden sind, und nicht in eine Sammelmeldung
   * am Kopf der Seite: Sonst wüsste bei vier Feldern niemand, welches gemeint ist.
   */
  async function logoHochladen(
    u: UnternehmenStammdaten, variante: LogoVariante, e: ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    const datei = e.target.files?.[0];
    // Leeren, damit dieselbe Datei nach einem Fehler erneut gewählt werden kann (sonst
    // löste die gleiche Auswahl kein change-Ereignis mehr aus).
    e.target.value = "";
    if (datei === undefined) return;
    const schluessel = logoSchluessel(u.id, variante);
    setLogoFehler((f) => ({ ...f, [schluessel]: "" }));
    setLogoArbeitet(schluessel);
    try {
      const neu = await client.uploadCompanyLogo(u.id, variante, datei);
      setZeilen((alt) => (alt === null ? alt : alt.map((x) => (x.id === neu.id ? neu : x))));
      setStempel((s) => ({ ...s, [schluessel]: Date.now() }));
    } catch (fehler) {
      const text = meldungAus(fehler, `„${datei.name}" konnte nicht hochgeladen werden.`);
      setLogoFehler((f) => ({ ...f, [schluessel]: text }));
    } finally {
      setLogoArbeitet(null);
    }
  }

  async function logoLoeschen(
    u: UnternehmenStammdaten, variante: LogoVariante,
  ): Promise<void> {
    const schluessel = logoSchluessel(u.id, variante);
    setLogoFehler((f) => ({ ...f, [schluessel]: "" }));
    setLogoArbeitet(schluessel);
    try {
      const neu = await client.deleteCompanyLogo(u.id, variante);
      setZeilen((alt) => (alt === null ? alt : alt.map((x) => (x.id === neu.id ? neu : x))));
    } catch (fehler) {
      const text = meldungAus(fehler, "Das Logo konnte nicht entfernt werden.");
      setLogoFehler((f) => ({ ...f, [schluessel]: text }));
    } finally {
      setLogoArbeitet(null);
    }
  }

  /** Die Länderauswahl eines Feldsatzes. Gehört ein gespeichertes Land (wider Erwarten)
   *  nicht zur gelieferten Liste, bleibt es als eigene Option wählbar — sonst zeigte die
   *  Auswahl still einen anderen Wert an, als der Server hält. */
  function landOptionen(gewaehlt: string): string[] {
    return laender.includes(gewaehlt) ? laender : [gewaehlt, ...laender];
  }

  const anlegeFehler = anlegeFormular.feldFehler;

  // ---------------------------------------------------------------------------
  // Die Objektliste — linke Spalte des Inhalts
  // ---------------------------------------------------------------------------
  const liste = (
    <>
      {/*
        * Der Anlege-Knopf steht ÜBER der Liste — genau dort, wo er im Bestand stand (die
        * Leiste über der Pflegeliste), und genau dort, wo ihn die Bereiche der Etappe 1
        * haben („Neues Angebot" im Filterkopf). Er ist während einer offenen
        * Bearbeitung gesperrt: Sein Erfolg liest die Liste neu.
        */}
      <div className="vw__leiste">
        <button type="button" className="vw__primaer" disabled={bearbeitung}
          onClick={() => {
            anlegeFormular.zuruecksetzen();
            setDialog(leererUnternehmensdialog());
          }}>
          Neues Unternehmen
        </button>
      </div>

      {ladeFehler !== null && <Meldung text={ladeFehler} />}
      {ladeFehler === null && zeilen === null && (
        <p className="vw__hinweis" role="status">Lädt …</p>
      )}
      {ladeFehler === null && zeilen !== null && zeilen.length === 0 && (
        <p className="vw__hinweis">Es gibt noch kein Unternehmen.</p>
      )}

      <Objektliste label="Unternehmen">
        {(zeilen ?? []).map((u) => (
          <Objektzeile
            key={u.id}
            name={u.name}
            kennung={u.shortCode}
            {...(u.active ? {} : { marken: ["stillgelegt"] })}
            gewaehlt={u.id === unternehmenId}
            /* Gesperrt bei offener Bearbeitung — dieselbe Regel wie überall: Die Liste soll
               unter einer laufenden Eingabe nichts anstossen können. */
            gesperrt={bearbeitung}
            /* Der zugängliche Name nennt Namen UND Kürzel; zehn Zeilen „Öffnen" wären für
               jemanden, der die Seite hört, nicht unterscheidbar. */
            ariaLabel={`Unternehmen ${u.name}, ${u.shortCode}, anzeigen`}
            onOeffnen={() => p.onOeffnen(u.id)}
            /* Die Hausfarbe als Feld VOR dem Namen — die eine benannte Ausnahme von „Zeile 2
               ist ein Text" (siehe ObjektzeileProps.vorn): Eine Farbe lässt sich nicht in
               Worten sagen. `aria-hidden`, weil sie für jemanden, der die Seite hört, keine
               Auskunft ist; das Kürzel daneben ist es. */
            vorn={<span className="vw__farbfeld" style={{ background: u.colorHex }}
              aria-hidden="true" />}
          />
        ))}
      </Objektliste>

      {/*
        * Der Anlege-Dialog steht bei seinem KNOPF, also bei der Liste — nicht im Detail.
        * Er gehört keinem Objekt, sondern dem Bereich, und überlebt deshalb einen
        * Objektwechsel (Lehre der Fixrunde 1: nur der ÄNDERN-Dialog fällt mit der Adresse).
        * Dass er auf dem iPhone auch dann sichtbar bleibt, wenn die Liste unter einem
        * geöffneten Objekt eingeklappt ist, hält Verwaltung.css ausdrücklich fest — sonst
        * stünde ein offener Dialog unsichtbar im Weg.
        */}
      {dialog !== null && (
        <Modal titel="Neues Unternehmen" onAbbrechen={anlegenAbbrechen}
          abbrechenGesperrt={anlegeFormular.laeuft}>
          {anlegeFormular.meldung !== null && <Meldung text={anlegeFormular.meldung} />}
          <Formular beschriftung="oben" {...anlegeFormular.formularProps}>
            <Feld id="vw-un-name" label="Name" bearbeitbar wert={dialog.name}
              onChange={(w) => {
                anlegeFormular.feldFehlerRaeumen("name");
                setDialog((d) => (d === null ? d : { ...d, name: w }));
              }}
              {...(anlegeFehler["name"] !== undefined
                ? { fehler: anlegeFehler["name"] } : {})} />
            {/* Das Kürzel ist HIER — und nur hier — eingebbar: Danach steht es in jeder
                vergebenen Nummer und ist unveränderlich. */}
            <Feld id="vw-un-shortCode" label="Kürzel (2–4 Großbuchstaben)" bearbeitbar
              wert={dialog.shortCode}
              onChange={(w) => {
                anlegeFormular.feldFehlerRaeumen("shortCode");
                setDialog((d) => (d === null ? d
                : { ...d, shortCode: w.toUpperCase() }));
              }}
              {...(anlegeFehler["shortCode"] !== undefined
                ? { fehler: anlegeFehler["shortCode"] } : {})} />
            <Farbwahl id="vw-un-colorHex" label="Farbe" wert={dialog.colorHex}
              unternehmensfarben={zeilen ?? []}
              gesperrt={anlegeFormular.zustand !== "bereit"}
              onChange={(w) => {
                anlegeFormular.feldFehlerRaeumen("colorHex");
                setDialog((d) => (d === null ? d : { ...d, colorHex: w }));
              }}
              {...(anlegeFehler["colorHex"] !== undefined
                ? { fehler: anlegeFehler["colorHex"] } : {})} />

            <p className="vw__hinweis">
              Anschrift, Bankverbindung und Kontakt sind wahlfrei — sie lassen sich
              jederzeit nachtragen.
            </p>
            {/* Straße und Hausnummer NEBENEINANDER, nicht untereinander — dieselbe
                Begründung wie im Adressen-Baustein (siehe .vw__strassenzeile). */}
            <div className="vw__strassenzeile">
              <Feld id="vw-un-street" label="Straße" bearbeitbar
                wert={dialog.street}
                onChange={(w) => {
                anlegeFormular.feldFehlerRaeumen("street");
                setDialog((d) => (d === null ? d : { ...d, street: w }));
              }}
                {...(anlegeFehler["street"] !== undefined
                  ? { fehler: anlegeFehler["street"] } : {})} />
              <Feld id="vw-un-houseNumber" label="Hausnummer" bearbeitbar
                wert={dialog.houseNumber}
                onChange={(w) => {
                anlegeFormular.feldFehlerRaeumen("houseNumber");
                setDialog((d) => (d === null ? d : { ...d, houseNumber: w }));
              }}
                {...(anlegeFehler["houseNumber"] !== undefined
                  ? { fehler: anlegeFehler["houseNumber"] } : {})} />
            </div>
            <Feld id="vw-un-postalCode" label="Postleitzahl" bearbeitbar
              wert={dialog.postalCode}
              onChange={(w) => {
                anlegeFormular.feldFehlerRaeumen("postalCode");
                setDialog((d) => (d === null ? d : { ...d, postalCode: w }));
              }}
              {...(anlegeFehler["postalCode"] !== undefined
                ? { fehler: anlegeFehler["postalCode"] } : {})} />
            <Feld id="vw-un-city" label="Ort" bearbeitbar wert={dialog.city}
              onChange={(w) => {
                anlegeFormular.feldFehlerRaeumen("city");
                setDialog((d) => (d === null ? d : { ...d, city: w }));
              }}
              {...(anlegeFehler["city"] !== undefined
                ? { fehler: anlegeFehler["city"] } : {})} />
            <Auswahl id="vw-un-country" name="vw-un-country" beschriftung="Land"
              wert={dialog.country}
              {...(anlegeFehler["country"] !== undefined
                ? { fehler: anlegeFehler["country"] } : {})}
              onGeaendert={(ev) => {
                anlegeFormular.feldFehlerRaeumen("country");
                setDialog((d) => (d === null ? d
                : { ...d, country: ev.wert }));
              }}>
              {landOptionen(dialog.country).map((code) => (
                <option key={code} value={code}>{landName(code)}</option>
              ))}
            </Auswahl>
            <Feld id="vw-un-vatId" label="USt-IdNr." bearbeitbar wert={dialog.vatId}
              onChange={(w) => {
                anlegeFormular.feldFehlerRaeumen("vatId");
                setDialog((d) => (d === null ? d : { ...d, vatId: w }));
              }}
              {...(anlegeFehler["vatId"] !== undefined
                ? { fehler: anlegeFehler["vatId"] } : {})} />
            <Feld id="vw-un-iban" label="IBAN" bearbeitbar wert={dialog.iban}
              onChange={(w) => {
                anlegeFormular.feldFehlerRaeumen("iban");
                setDialog((d) => (d === null ? d : { ...d, iban: w }));
              }}
              {...(anlegeFehler["iban"] !== undefined
                ? { fehler: anlegeFehler["iban"] } : {})} />
            <Feld id="vw-un-bic" label="BIC" bearbeitbar wert={dialog.bic}
              onChange={(w) => {
                anlegeFormular.feldFehlerRaeumen("bic");
                setDialog((d) => (d === null ? d : { ...d, bic: w }));
              }}
              {...(anlegeFehler["bic"] !== undefined
                ? { fehler: anlegeFehler["bic"] } : {})} />
            {/* Der Verkäufer-Kontakt (014) ist beim Anlegen WAHLFREI wie Anschrift und
                Bankverbindung — wer ihn schon kennt, soll ihn aber nicht nachtragen müssen:
                Sonst verletzt die erste XRechnung dieses Unternehmens BR-DE-5/6/7. */}
            <Feld id="vw-un-contactName" label="Kontakt (Name der Stelle)" bearbeitbar
              wert={dialog.contactName}
              onChange={(w) => {
                anlegeFormular.feldFehlerRaeumen("contactName");
                setDialog((d) => (d === null ? d
                : { ...d, contactName: w }));
              }}
              {...(anlegeFehler["contactName"] !== undefined
                ? { fehler: anlegeFehler["contactName"] } : {})} />
            <Feld id="vw-un-phone" label="Kontakt-Telefon" bearbeitbar wert={dialog.phone}
              onChange={(w) => {
                anlegeFormular.feldFehlerRaeumen("phone");
                setDialog((d) => (d === null ? d : { ...d, phone: w }));
              }}
              {...(anlegeFehler["phone"] !== undefined
                ? { fehler: anlegeFehler["phone"] } : {})} />
            <Feld id="vw-un-email" label="Kontakt-E-Mail" bearbeitbar wert={dialog.email}
              onChange={(w) => {
                anlegeFormular.feldFehlerRaeumen("email");
                setDialog((d) => (d === null ? d : { ...d, email: w }));
              }}
              {...(anlegeFehler["email"] !== undefined
                ? { fehler: anlegeFehler["email"] } : {})} />

            {/* Die vier Nummernkreise entstehen in DERSELBEN Transaktion wie das
                Unternehmen. Der Startwert ist der Zählerstand VOR der ersten Nummer: Bei 500
                trägt der erste Kunde die 501. Gedacht ist er für die Übernahme aus einem
                Altsystem — später lässt er sich nur ändern, solange keine Nummer vergeben
                ist. */}
            <fieldset className="vw__startwerte">
              <legend className="vw__label">Startwerte der Nummernkreise</legend>
              {KREISE.map((kreis) => (
                <Feld key={kreis} id={`vw-un-start-${kreis}`}
                  label={`Startwert ${ENTITY_LABEL[kreis]}`} bearbeitbar
                  wert={dialog.startwerte[kreis]}
                  onChange={(w) => {
                anlegeFormular.feldFehlerRaeumen(`startValue.${kreis}`);
                setDialog((d) => (d === null ? d
                    : { ...d, startwerte: { ...d.startwerte, [kreis]: w } }));
              }}
                  {...(anlegeFehler[`startValue.${kreis}`] !== undefined
                    ? { fehler: anlegeFehler[`startValue.${kreis}`] } : {})} />
              ))}
            </fieldset>
          </Formular>

          <ModalFuss speichernText="Anlegen"
            speichernGesperrt={anlegeFormular.zustand !== "bereit"}
            abbrechenGesperrt={anlegeFormular.laeuft}
            onSpeichern={() => { void anlegen(); }} onAbbrechen={anlegenAbbrechen} />
        </Modal>
      )}
    </>
  );

  // ---------------------------------------------------------------------------
  // Das Detail — das Formular GENAU EINES Unternehmens
  // ---------------------------------------------------------------------------
  const detail = ((): ReactElement => {
    if (unternehmenId === null) {
      return (
        <p className="vw__hinweis">
          Links ein Unternehmen wählen — Name, Farbe, Anschrift, USt-IdNr.,
          Bankverbindung, Kontakt und Logos stehen dann hier. Das Kürzel ist
          unveränderlich: Es steht am Anfang jeder bereits vergebenen Nummer.
        </p>
      );
    }
    if (satz === null) {
      /*
       * Die dreistufige Auskunft für ein Lesezeichen auf ein Unternehmen, das die Liste
       * nicht enthält (Etappe-1-Lektion): „steht nicht in der Liste" darf erst behauptet
       * werden, wenn es eine Liste GIBT. Solange sie lädt, wäre die Aussage falsch; ist
       * sie gescheitert, liegt es nicht am Unternehmen.
       */
      if (ladeFehler !== null) return <Meldung text={ladeFehler} />;
      if (zeilen === null) return <p className="vw__hinweis">Lädt …</p>;
      return (
        <p className="vw__hinweis">
          Dieses Unternehmen gibt es nicht (mehr) — bitte links eines aus der Liste wählen.
        </p>
      );
    }

    const u = satz;
    const e = entwurfVon(u);
    const geaendert = istGeaendert(u);
    const fehler = aenderFormular.feldFehler as Partial<Record<UnternehmenFeld, string>>;
    const meldung = meldungen[u.id] ?? "";
    const erfolg = erfolge[u.id] ?? "";
    /* Panel und Rückfrage gehören GENAU DEM Unternehmen, über dem sie geöffnet wurden
       (Fixrunde 1): Ohne diesen Vergleich überlebten sie einen Adresswechsel, den dieses
       Detail nicht auslöst — die Zurück-Taste etwa —, und ihre Knöpfe arbeiteten dann am
       Unternehmen A, während B darunter stünde. */
    const panelOffen = aendern === u.id;

    /** Abbrechen — mit Rückfrage, wenn ein Entwurf offen ist, und dann VERWIRFT es ihn.
     *  Anders sagte die Rückfrage die Unwahrheit: Die Entwürfe liegen je Kennung und
     *  stünden beim nächsten Öffnen wieder da (dieselbe Lehre wie beim Verlassen-Wächter
     *  oben, Fixrunde 1). */
    async function abbrechen(): Promise<void> {
      // Kein Ausgang, solange geschrieben wird — Escape wie Knopf (Fixrunde 1).
      if (aenderFormular.laeuft) return;
      if (!(await darfAbbrechen(geaendert))) return;
      vergissEntwurf(u.id);
      aenderFormular.zuruecksetzen();
      setAendern(null);
    }

    /** Eine Zeile des LESENDEN Details — „nicht gepflegt", wo nichts steht. Eine leere
     *  Zeile verschwiege den Unterschied zwischen „leer" und „gibt es hier nicht". */
    function zeile(label: string, wert: string): ReactElement {
      return (
        <span className="vw__eintrag-zusatz">
          {label}: {wert.trim() === "" ? "nicht gepflegt" : wert}
        </span>
      );
    }

    const anschrift = [u.street, [u.postalCode, u.city].filter((x) => x !== null
      && x !== "").join(" "), landName(u.country)]
      .filter((x) => x !== null && x !== "").join(", ");

    return (
      /*
       * Eigene Identität je Unternehmen (Etappe-1-Lektion `key={objekt.id}`): Ohne diesen
       * Schlüssel nähme ein Adresswechsel den DOM-Zustand dieses Bereichs auf das nächste
       * Unternehmen mit — allen voran die vier Dateiauswahlen der Logos, deren Inhalt
       * React nicht führt.
       */
      <div key={u.id} className="vw__eintrag">
        {/* Der Kopf zeigt den GESPEICHERTEN Stand: Er ist die Kennung des Satzes. Das
            Kürzel steht als Marke und war noch nie ein Eingabefeld — es ist
            unveränderlich. */}
        <div className="vw__eintrag-kopf">
          <span className="vw__farbfeld" style={{ background: u.colorHex }}
            aria-hidden="true" />
          <span className="vw__eintrag-name">{u.name}</span>
          <span className="vw__marke">{u.shortCode}</span>
          {!u.active && <span className="vw__marke">stillgelegt</span>}
          {geaendert && <span className="vw__marke">ungespeichert</span>}
        </div>

        {/*
          * Das Detail LIEST (Hausregel „striktes Modalpanel", 06.09.2026): Die zwölf
          * pflegbaren Felder stehen hier als Text, geändert wird im Panel. Bis dahin war
          * das Detail SELBST das Formular — man konnte hineintippen, weggehen und nie
          * erfahren, dass etwas Ungespeichertes zurückblieb.
          */}
        {zeile("Anschrift", anschrift)}
        {zeile("USt-IdNr.", u.vatId ?? "")}
        {zeile("Bankverbindung",
          [u.iban, u.bic].filter((x) => x !== null && x !== "").join(" · "))}
        {zeile("Kontakt",
          [u.contactName, u.phone, u.email]
            .filter((x) => x !== null && x !== "").join(" · "))}

        {/* Der Block „DATEV" (Aufgabe 9) — unter den Stammdaten, über den Logos. Er lädt
            seinen eigenen Stand (`getDatevKopplung`) und räumt beim Unternehmenswechsel
            selbst ab (`key={u.id}` am umschliessenden `<div>` reicht dafür bereits: eine
            andere Kennung ist eine andere Instanz mit frischem Zustand). */}
        <DatevBlock client={p.client} companyId={u.id} />

        {/* Die vier Logo-Varianten bleiben IM DETAIL und wandern nicht ins Panel: Sie
            haben kein Speichern und kein Abbrechen — ein Hochladen ist sofort geschehen,
            ein Entfernen auch. In einem Panel mit der Fußleiste Speichern/Abbrechen wäre
            das eine Lüge: „Abbrechen" nähme die hochgeladene Datei nicht zurück.

            SVG, seit Entscheid 25 auch PNG oder JPEG (bis 2000 × 2000 Pixel). Die
            Dateiauswahl schränkt auf diese Endungen ein — das ist Bequemlichkeit,
            keine Sicherheit: Geprüft wird an den BYTES auf dem Server, und dessen Meldung
            steht unten an der Variante.

            ZWEI QUELLEN, EINE ZEILE (11.09.2026): Jede Variante zeigt das WIRKSAME Logo
            und dazu seine Herkunft — „hochgeladen" oder „Standarddatei <name>". „Kein
            Logo" steht nur noch, wo es beides nicht gibt. Entfernen gibt es ausschliesslich
            für Hochgeladenes: Die Standarddatei liegt im Auslieferungspaket der Oberfläche
            und wäre von hier aus gar nicht zu löschen — nach dem Entfernen fällt die Zeile
            auf sie zurück, wenn es eine gibt. */}
        <fieldset className="vw__logos">
          <legend className="vw__label">Logos (SVG, PNG oder JPEG)</legend>
          {LOGO_VARIANTEN.map((variante) => {
            const schluessel = logoSchluessel(u.id, variante);
            // `vorhanden` heisst HOCHGELADEN — nur das liegt in der Ablage und lässt sich
            // deshalb entfernen. Ob überhaupt ein Logo WIRKT, sagt `wirksam`: Es kann
            // auch die Standarddatei aus dem Auslieferungspaket sein.
            const vorhanden = u.hochgeladeneLogos.includes(variante);
            const logofehler = logoFehler[schluessel] ?? "";
            const arbeitet = logoArbeitet === schluessel;
            const wirksam = logoVorschau(u, variante, stempel[schluessel]);
            return (
              <div key={variante} className="vw__logo">
                <span className="vw__logo-name">{LOGO_LABEL[variante]}</span>
                {wirksam === null
                  ? <span className="vw__logo-leer">kein Logo</span>
                  : (
                    <>
                      <img className="vw__logo-bild" src={wirksam.url}
                        alt={`${LOGO_LABEL[variante]} von ${u.name}`} />
                      <span className="vw__logo-herkunft">{herkunftText(wirksam)}</span>
                    </>
                  )}
                <label className="vw__logo-wahl">
                  {/* „Ersetzen", sobald IRGENDEINE Quelle wirkt — auch die
                      Standarddatei: Ein Hochladen schiebt sich vor sie und ist aus
                      Sicht des Bedieners genau das, ein Ersetzen. „Hochladen" bliebe
                      nur dort ehrlich, wo bisher nichts zu sehen ist. */}
                  <span className="vw__knopf">
                    {wirksam !== null ? "Ersetzen" : "Hochladen"}
                  </span>
                  <input type="file" className="vw__sr" accept={BILD_ACCEPT}
                    disabled={arbeitet}
                    aria-label={`${LOGO_LABEL[variante]} von ${u.name} hochladen`}
                    onChange={(ev) => { void logoHochladen(u, variante, ev); }} />
                </label>
                {/* Nur für HOCHGELADENES — und darum an `vorhanden` und nicht an
                    `wirksam`: Ein „Entfernen" an einer Standarddatei verspräche eine
                    Wirkung, die es nicht gibt. */}
                {vorhanden && (
                  <button type="button" className="vw__knopf" disabled={arbeitet}
                    aria-label={`${LOGO_LABEL[variante]} von ${u.name} entfernen`}
                    onClick={() => { void logoLoeschen(u, variante); }}>
                    Entfernen
                  </button>
                )}
                {logofehler !== "" && <Hinweis art="fehler">{logofehler}</Hinweis>}
              </div>
            );
          })}
        </fieldset>

        {/*
          * DIESELBE Karte wie in der Kundenakte (Aufgabe 8) und an den Kontaktwegen
          * (Aufgabe 9), nur mit dem Besitzer `companies`. Sie bleibt IM DETAIL und wandert
          * nicht ins Stammdaten-Panel: Adressen haben ihr eigenes Speichern und ihr eigenes
          * Abbrechen (jede ist ein eigener Satz) — in einem Panel mit der Fussleiste
          * Speichern/Abbrechen wäre das eine Lüge, genau wie bei den Logos darüber.
          *
          * `key`: OBJEKT und VERSION. Ohne die Kennung nähme ein Unternehmenswechsel den
          * inneren Zustand der Karte (Panel, Entwurf, Meldung) auf das nächste Unternehmen
          * mit; ohne die Version zeigte die Karte nach einem Stammdaten-Speichern (das auf
          * die Hauptadresse durchschreibt) ihren alten Bestand weiter, weil sich weder
          * `client` noch `ownerId` dabei ändern (Muster `wegeVersion` in
          * KundeAnsprechpartner.tsx).
          *
          * `nurLesen={!istAdmin}`: Schreiben darf nur ADMIN, der Server weist alles andere
          * mit 403 ab.
          *
          * `verdeckt={panelOffen}`: Sperrt die Knöpfe der Karte, solange das
          * Stammdaten-Panel darüber liegt — in jsdom fängt nichts den Klick ab, und ein
          * Test griffe sonst hinter das Panel (dieselbe Überlegung wie bei `verdeckt` in
          * KundePortalMandanten).
          */}
        <AdressListe key={`${u.id}-${adressVersion}`} client={client} besitzer="companies"
          ownerId={u.id} titel="Adressen und Kontaktwege" namensVorschlag={u.name}
          nurLesen={!istAdmin} verdeckt={panelOffen}
          onGeaendert={() => { void nachAdressaenderung(); }} />

        <div className="vw__eintrag-aktionen">
          <button type="button" className="vw__knopf"
            aria-label={`Stammdaten von ${u.name} ändern`}
            onClick={() => { aenderFormular.zuruecksetzen(); setAendern(u.id); }}>
            Ändern
          </button>
          <button type="button" className="vw__knopf" disabled={schaltet === u.id}
            aria-label={u.active
              ? `${u.name} stilllegen` : `${u.name} wieder einschalten`}
            onClick={() => {
              // Nur das Stilllegen fragt nach — es nimmt das Unternehmen aus allen
              // Auswahlen; das Wiedereinschalten stellt her, was vorher war.
              if (u.active) setStilllegen(u);
              else void umschalten(u, true);
            }}>
            {u.active ? "Stilllegen" : "Wieder einschalten"}
          </button>
        </div>
        {/* Fehler vor Erfolg (Schlechtestes oben); getrennte Arten und Rollen —
            eine gelungene Speicherung ist kein Alarm (Lehre 3b-2-W1). */}
        {meldung !== "" && !panelOffen && <Meldung text={meldung} />}
        {erfolg !== "" && <Hinweis art="ok">{erfolg}</Hinweis>}

        {/* Das Änderungspanel — die zwölf pflegbaren Felder, mit der Fußleiste
            Speichern/Abbrechen. Es steht bei seinem Knopf, also im Detail. */}
        {panelOffen && (
          <Modal titel={`Stammdaten von ${u.name}`} onAbbrechen={abbrechen}
            abbrechenGesperrt={aenderFormular.laeuft}>
            {aenderFormular.meldung !== null && <Meldung text={aenderFormular.meldung} />}
            <Formular beschriftung="oben" {...aenderFormular.formularProps}>
              <Feld id={`vw-u-name-${u.id}`} label="Name" bearbeitbar
                wert={e.name} onChange={(w) => aendere(u, { name: w })}
                {...(fehler.name !== undefined ? { fehler: fehler.name } : {})} />
              <Farbwahl id={`vw-u-colorHex-${u.id}`} label="Farbe" wert={e.colorHex}
                unternehmensfarben={zeilen ?? []}
                gesperrt={aenderFormular.zustand !== "bereit"}
                onChange={(w) => aendere(u, { colorHex: w })}
                {...(fehler.colorHex !== undefined ? { fehler: fehler.colorHex } : {})} />
              <div className="vw__strassenzeile">
                <Feld id={`vw-u-street-${u.id}`} label="Straße" bearbeitbar
                  wert={e.street} onChange={(w) => aendere(u, { street: w })}
                  {...(fehler.street !== undefined ? { fehler: fehler.street } : {})} />
                <Feld id={`vw-u-houseNumber-${u.id}`} label="Hausnummer" bearbeitbar
                  wert={e.houseNumber} onChange={(w) => aendere(u, { houseNumber: w })}
                  {...(fehler.houseNumber !== undefined
                    ? { fehler: fehler.houseNumber } : {})} />
              </div>
              <Feld id={`vw-u-postalCode-${u.id}`} label="Postleitzahl" bearbeitbar
                wert={e.postalCode} onChange={(w) => aendere(u, { postalCode: w })}
                {...(fehler.postalCode !== undefined
                  ? { fehler: fehler.postalCode } : {})} />
              <Feld id={`vw-u-city-${u.id}`} label="Ort" bearbeitbar
                wert={e.city} onChange={(w) => aendere(u, { city: w })}
                {...(fehler.city !== undefined ? { fehler: fehler.city } : {})} />
              <Auswahl id={`vw-u-country-${u.id}`} name={`vw-u-country-${u.id}`}
                beschriftung="Land" wert={e.country}
                {...(fehler.country !== undefined ? { fehler: fehler.country } : {})}
                onGeaendert={(ev) => aendere(u, { country: ev.wert })}>
                {landOptionen(e.country).map((code) => (
                  <option key={code} value={code}>{landName(code)}</option>
                ))}
              </Auswahl>
              <Feld id={`vw-u-vatId-${u.id}`} label="USt-IdNr." bearbeitbar
                wert={e.vatId} onChange={(w) => aendere(u, { vatId: w })}
                {...(fehler.vatId !== undefined ? { fehler: fehler.vatId } : {})} />
              {/* Bankverbindung (Migration 011): der Absender auf der E-Rechnung
                  (BG-16/BT-84). Darstellungsformen wie Vierergruppen oder Kleinschreibung
                  sind erlaubt — der Server normalisiert auf die kompakte Großform und
                  antwortet mit ihr (normalizeCompanyMaster). */}
              <Feld id={`vw-u-iban-${u.id}`} label="IBAN" bearbeitbar
                wert={e.iban} onChange={(w) => aendere(u, { iban: w })}
                {...(fehler.iban !== undefined ? { fehler: fehler.iban } : {})} />
              <Feld id={`vw-u-bic-${u.id}`} label="BIC" bearbeitbar
                wert={e.bic} onChange={(w) => aendere(u, { bic: w })}
                {...(fehler.bic !== undefined ? { fehler: fehler.bic } : {})} />
              {/* Verkäufer-Kontakt (Migration 014): BT-41/42/43 der E-Rechnung. XRechnung
                  verlangt alle drei (BR-DE-5/6/7); die Maske erzwingt sie trotzdem nicht —
                  ob ein Beleg als XRechnung hinausdarf, entscheidet das
                  Vollständigkeits-Gate der Engine, nicht dieses Formular. */}
              <Feld id={`vw-u-contactName-${u.id}`} label="Kontakt (Name der Stelle)"
                bearbeitbar wert={e.contactName}
                onChange={(w) => aendere(u, { contactName: w })}
                {...(fehler.contactName !== undefined
                  ? { fehler: fehler.contactName } : {})} />
              <Feld id={`vw-u-phone-${u.id}`} label="Kontakt-Telefon" bearbeitbar
                wert={e.phone} onChange={(w) => aendere(u, { phone: w })}
                {...(fehler.phone !== undefined ? { fehler: fehler.phone } : {})} />
              <Feld id={`vw-u-email-${u.id}`} label="Kontakt-E-Mail" bearbeitbar
                wert={e.email} onChange={(w) => aendere(u, { email: w })}
                {...(fehler.email !== undefined ? { fehler: fehler.email } : {})} />
            </Formular>

            <ModalFuss speichernText="Speichern"
              speichernGesperrt={!geaendert || aenderFormular.zustand !== "bereit"}
              /* Nur während des laufenden Schreibens — nicht an `geaendert`: Ein Panel
                 ohne Eingabe muss gerade DANN abbrechbar sein. */
              abbrechenGesperrt={aenderFormular.laeuft}
              speichernLabel={`Stammdaten von ${u.name} speichern`}
              onSpeichern={() => { void speichern(u); }}
              onAbbrechen={abbrechen} />
          </Modal>
        )}

        {/* Die Rückfrage steht bei ihrem Knopf, also im Detail — anders als der
            Anlege-Dialog oben, der bei seinem Knopf über der Liste steht. Sie gehört
            dabei GENAU DEM Unternehmen, über dem sie geöffnet wurde (Fixrunde 1): Ohne
            den Vergleich überlebte sie einen Adresswechsel, den dieses Detail nicht
            auslöst — die Zurück-Taste etwa —, und ihr Knopf legte dann das Unternehmen A
            still, während B darunter stünde. */}
        {stilllegen !== null && stilllegen.id === u.id && (
          <Modal titel={`${stilllegen.name} stilllegen?`}
            /* KEIN Warte-Riegel nötig — anders als die Archivieren-Frage der Konditionen
               schliesst diese Rückfrage beim Bestätigen SOFORT (umschalten() räumt sie als
               Erstes); der laufende Server-Weg gehört danach der Zeile (schaltet), nie
               diesem Panel. Ein Riegel hier wäre toter Code. */
            onAbbrechen={() => setStilllegen(null)}>
            <p className="vw__hinweis">
              Stillgelegte Unternehmen verschwinden aus allen Auswahlen und aus der
              Sichtbarkeit — Kunden, Vorgänge und festgeschriebene Rechnungen bleiben
              unverändert bestehen. Rückgängig zu machen ist das jederzeit: Das Unternehmen
              bleibt in der Liste stehen.
            </p>
            <ModalFuss speichernText="Stilllegen"
              onSpeichern={() => { void umschalten(stilllegen, false); }}
              onAbbrechen={() => setStilllegen(null)} />
          </Modal>
        )}
      </div>
    );
  })();

  return { liste, detail: <>{detail}{bestaetigung}</> };
}
